// src/components/TableauStyleVertical.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useLayoutEffect,
} from "react";
import { debounce } from "../utils/debounce";
import axiosInstance from "../axiosInstance";
import DOMPurify from "dompurify";

// === WYSIWYG identique à l’intro/mentions ===
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import { createPortal } from "react-dom";
import { loadTableTheme, saveTableTheme, themeStyleVars, DEFAULT_TABLE_THEME } from "../utils/tableTheme";
/* ================== Constantes overlay suggestions ================== */
const SUG_ITEM_MIN_H = 92; // ~ hauteur min d’un item
const SUG_MIN_VISIBLE = 3; // au moins 3 items
const SUG_MAX_VISIBLE = 6; // max 6 items
const SUG_Z = 2147483647;  // au-dessus de tout
// Helpers de classes de cellules (évite les "undefined")
const tdCell = "align-top px-2 py-1 text-sm border-b border-slate-200";
const tdCellNote = "align-top px-2 py-1 text-sm italic text-slate-700 border-b-0";


// Attributor 'size' robuste (évite double registre en StrictMode)
try {
  const SizeStyle = Quill.import("attributors/style/size");
  SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
  if (!window.__PL_SIZE_REGISTERED__) {
    Quill.register(SizeStyle, true);
    window.__PL_SIZE_REGISTERED__ = true;
  }
} catch { }

/* ---------- helpers texte ---------- */
const SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "24px", "32px"];
const quillFormats = [
  "size",
  "bold",
  "italic",
  "underline",
  "align",
  "list",
  "bullet",
  "link",
  "clean",
];

const isProbablyHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(s);
const textToHtml = (txt = "") => {
  const esc = (t) =>
    t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = String(txt || "")
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`);
  return blocks.join("") || "<p></p>";
};
const ensureHtml = (val = "") => (isProbablyHtml(val) ? val : textToHtml(val));
const htmlToText = (html = "") => {
  let s = String(html || "");
  s = s.replace(/<\s*br\s*\/?>/gi, "\n");
  s = s.replace(/<\/p>\s*<p>/gi, "\n\n");
  s = s.replace(/<\/?p[^>]*>/gi, "");
  s = s.replace(/<[^>]+>/g, "");
  s = s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s.trim();
};
const stripTags = (s = "") => String(s).replace(/<[^>]+>/g, " ");
const norm = (s = "") =>
  stripTags(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
// Helper pour auto-ajuster la hauteur d'un textarea
const autoResizeTextarea = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
};

/* ===== Styles de base ===== */
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
  tableLayout: "auto",
};
const baseVBorder = "0.5px solid var(--doc-vline)";
const cellStyle = {
  borderLeft: baseVBorder,
  borderRight: baseVBorder,       
  padding: "4px 6px",
  verticalAlign: "middle",
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const thStyle = {
  ...cellStyle,
  background: "var(--doc-th-bg)",
  color: "var(--doc-th-text)",
  fontWeight: 600,
};
const designationCellStyle = {
  ...cellStyle,
  textAlign: "left",
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflow: "visible",
  position: "relative",
};
const designationStyle = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: "0.85rem",
  fontFamily: "inherit",
  background: "transparent",
  resize: "none",
  overflow: "hidden",
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: "1.4",
};
const numericWrapper = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "4px",
  flexWrap: "nowrap",
  position: "relative",
};
const numericInputStyle = {
  border: "none",
  background: "transparent",
  outline: "none",
  fontSize: "0.85rem",
  fontFamily: "inherit",
  padding: 0,
  margin: 0,
  appearance: "none",
  MozAppearance: "textfield",
  WebkitAppearance: "none",
  textAlign: "right",
  overflow: "hidden",
};
const unitSuffixStyle = {
  fontSize: "0.85rem",
  whiteSpace: "nowrap",
  marginLeft: "2px",
};
const unitDropdownInputStyle = {
  width: "50px",
  padding: "2px 4px",
  fontSize: "0.75rem",
  border: "1px solid #aaa",
  borderRadius: "3px",
  background: "white",
  textAlign: "center",
};
/* Unités proposées (mêmes que Désignation) */
const UNIT_OPTIONS = [
  "m²", "m³", "ml", "m", "cm", "mm", "h", "u", "kg", "g", "L", "cl", "mL", "pièce", "lot", "forfait", "jour"
];

/* Style de l’input d’unité quand il est en édition (centré + largeur fixe) */
const unitInputListStyle = {
  ...unitDropdownInputStyle,
  textAlign: "center",
  width: 64,
};

const deleteBtnStyle = {
  background: "none",
  border: "none",
  color: "#111827",
  cursor: "pointer",
  fontSize: "1rem",
  lineHeight: 1,
};

const quantiteCellStyle = { ...cellStyle, minWidth: "50px", width: "60px" };
const puCellStyle = { ...cellStyle, minWidth: "60px", width: "70px" };
const tvaCellStyle = { ...cellStyle, minWidth: "50px", width: "60px" };

const pageBreakBarStyle = {
  margin: "6px 0 8px",
  padding: "6px 8px",
  borderRadius: 8,
  background: "#f8fafc",
  border: "1px dashed #cbd5e1",
  color: "#475569",
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "grab",
  justifyContent: "center",
  position: "relative",
};
const chipBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  border: "1px solid #cbd5e1",
  background: "white",
  color: "#334155",
  borderRadius: 6,
  fontSize: 12,
  padding: "2px 8px",
  cursor: "pointer",
};

/* ========= Overlay Suggestions via Portal ========= */
/* ========= Overlay Suggestions via Portal (version stable) ========= */
function SuggestionsOverlay({
  rect,
  list,
  activeIdx,
  onPick,
  onHover,
  onClose,
  tvaParDefaut,
  isPdf = false,
}) {

  // 1) Hooks TOUJOURS au top-level (jamais derrière un return)
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const onDoc = () => onCloseRef.current?.();
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // 2) Court-circuit APRÈS les hooks (donc pas de variation d'ordre)
  if (!rect || !list || list.length === 0) return null;

  // 3) Pas de hook pour la taille viewport -> on lit window au render (OK)
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;

  const width = Math.min(Math.max(rect.width, 520), 820);
  const desiredMinH = SUG_ITEM_MIN_H * SUG_MIN_VISIBLE;
  const desiredMaxH = SUG_ITEM_MIN_H * SUG_MAX_VISIBLE;

  const roomDown = vh - rect.bottom - 8;
  const roomUp = rect.top - 8;
  const openUp = roomDown < desiredMinH && roomUp > roomDown;

  const style = {
    position: "fixed",
    zIndex: SUG_Z,
    left: Math.max(8, Math.min(rect.left, vw - 8 - width)),
    width,
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    boxShadow: "0 8px 20px rgba(0,0,0,.08)",
    overflowY: "auto",
    fontSize: 12,
    minHeight: desiredMinH,
    maxHeight: desiredMaxH,
    top: openUp ? undefined : Math.min(rect.bottom + 4, vh - 8 - desiredMinH),
    bottom: openUp ? Math.max(8, vh - rect.top + 4) : undefined,
  };

  const stop = (e) => e.stopPropagation();
  const itemStyle = {
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
    background: "white",
    minHeight: SUG_ITEM_MIN_H,
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    gap: 4,
  };

  return createPortal(
    <div style={style} onMouseDown={stop}>
      {list.map((d, i) => (
        <button
          key={d.id || d.label || i}
          type="button"
          onMouseEnter={() => onHover?.(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick?.(d);
          }}
          style={{ ...itemStyle, background: i === activeIdx ? "#f1f5f9" : "white" }}
        >
{isProbablyHtml(d.label) ? (
  <div
    className="ql-editor !p-0"
    style={{
      lineHeight: 1.35,

      // IMPORTANT : en PDF on ne clamp pas
      display: isPdf ? "block" : "-webkit-box",
      WebkitLineClamp: isPdf ? "unset" : 3,
      WebkitBoxOrient: isPdf ? "unset" : "vertical",
      overflow: isPdf ? "visible" : "hidden",

      // anti-rognage html2canvas (descenders)
      paddingBottom: isPdf ? "2px" : 0,
    }}
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(ensureHtml(d.label)),
    }}
  />
) : (
  <div style={{ lineHeight: 1.35, whiteSpace: "normal" }}>
    {stripTags(d.label)}
  </div>
)}


          <div style={{ color: "#64748b" }}>
            {Number(d.prixUnitaire || 0).toFixed(2)} € · TVA{" "}
            {d.tva === 0 || Number.isFinite(d.tva) ? d.tva : tvaParDefaut}%
          </div>
        </button>
      ))}
    </div>,
    document.body
  );
}

/* ========= Sous-composant : ligne NOTE avec WYSIWYG ========= */
function NoteRow({
  rowIndex,
  ligne,
  lectureSeule,
  onEditOpen,
  isEditing,
  onDone,
  onDelete,
  handleChange,
  rowRef,
  tdBase,
  isPdf = false,      // ✅ optionnel
}) {
  const toolbarId = `toolbar-note-${rowIndex}`;

  const modules = useMemo(
    () => ({ toolbar: { container: `#${toolbarId}` } }),
    [toolbarId]
  );

  const safeHtml = useMemo(
    () => DOMPurify.sanitize(ensureHtml(ligne?.designation || "")),
    [ligne?.designation]
  );

  return (
<tr ref={rowRef}>
  {/* 1) cellule vide pour la colonne N° => la note commence comme les autres lignes */}
  <td
    className="col--num"
    style={{
      ...cellStyle,
      textAlign: "center",
      verticalAlign: "top",
      overflow: "visible",
      whiteSpace: "nowrap",
    }}
  />

  {/* 2) cellule NOTE qui commence sous "Désignation" (et s'étend jusqu'à Total HT) */}
  <td
  colSpan={5} // libellé + qté + pu + tva + total
  className={`col--libelle note-td ${tdCellNote} text-left`}
  style={{
    ...cellStyle,

    // IMPORTANT : on neutralise les règles "ligne standard"
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    verticalAlign: "top",
    textAlign: "left",

    // IMPORTANT : on enlève le "gros vide" que tu as créé
    paddingTop: "0px",
    paddingBottom: "0px",
  }}
>

    {isEditing ? (
      <div data-note-editor="1">
        <div id={toolbarId} className="ql-toolbar ql-snow rounded-t-md mb-1">
          <select className="ql-size" defaultValue="">
            <option value="">Défaut</option>
            {SIZE_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {parseInt(v, 10)}
              </option>
            ))}
          </select>
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
          <select className="ql-align" />
          <button className="ql-list" value="ordered" />
          <button className="ql-list" value="bullet" />
          <button className="ql-link" />
          <button className="ql-clean" />

          <div className="inline-block ml-2">
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
              onClick={onDone}
              title="Terminer l’édition"
            >
              Terminer
            </button>
          </div>
        </div>

        <ReactQuill
          theme="snow"
          value={ensureHtml(ligne?.designation || "")}
          onChange={(html) => handleChange(rowIndex, "designation", html)}
          modules={modules}
          formats={quillFormats}
          className="rounded-b-md bg-white"
        />
      </div>
    ) : (
<div
  className={`ql-editor note-ql !p-0 ${!ligne?.designation ? "text-gray-400 italic" : ""}`}
  style={{
    lineHeight: 1.15,       // plus “collé” comme ton aperçu
    cursor: lectureSeule ? "default" : "text",
    padding: 0,
    margin: 0,
    overflow: "visible",
    whiteSpace: "normal",
  }}

        onClick={() => !lectureSeule && onEditOpen(rowIndex)}
        dangerouslySetInnerHTML={{
          __html: safeHtml || "<em>Ajouter une note libre…</em>",
        }}
      />
    )}
  </td>

  {/* 3) cellule actions (X) si édition */}
  {!lectureSeule && (
    <td className="col--actions" style={cellStyle}>
      <button onClick={onDelete} style={deleteBtnStyle} title="Supprimer">
        ×
      </button>
    </td>
  )}
</tr>

  );
}

// remplace ENTIEREMENT l’ancien RemisesModal par ceci
function RemisesModal({
  onClose,
  globalRemise,
  onChangeGlobalRemise,
  onClearGlobalRemise,
}) {
  const btnBlue = {
    padding: "8px 12px", borderRadius: 8, border: "none",
    color: "#fff", background: "#2563eb", cursor: "pointer"
  };
  const btnOutline = {
    padding: "8px 12px", borderRadius: 8,
    border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer"
  };
  const input = { height: 36, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px" };

  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.28)", zIndex: 2147483646 }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed", zIndex: 2147483647, top: "6vh", left: "50%", transform: "translateX(-50%)",
          width: "min(560px, 96vw)", background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
          boxShadow: "0 20px 60px rgba(0,0,0,.18)", padding: 16
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8
        }}>
          <h3 style={{ fontWeight: 700, fontSize: 18 }}>Remise globale</h3>
          <button style={btnOutline} onClick={onClose}>Fermer</button>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {/* Choix du mode */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Mode</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                style={btnOutline}
                onClick={() =>
                  onChangeGlobalRemise({ enabled: true, mode: "percent", value: Number(globalRemise?.value || 0) })
                }
              >
                %
              </button>
              <button
                style={btnOutline}
                onClick={() =>
                  onChangeGlobalRemise({ enabled: true, mode: "ht", value: Number(globalRemise?.value || 0) })
                }
              >
                HT
              </button>
              <button
                style={btnOutline}
                onClick={() =>
                  onChangeGlobalRemise({ enabled: true, mode: "ttc", value: Number(globalRemise?.value || 0) })
                }
              >
                TTC
              </button>
            </div>
          </div>

          {/* Valeur */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Valeur</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="number"
                step={globalRemise?.mode === "percent" ? "0.1" : "0.01"}
                min="0"
                value={globalRemise?.value ?? 0}
                onChange={(e) =>
                  onChangeGlobalRemise({
                    enabled: true,
                    mode: globalRemise?.mode || "percent",
                    value: Number(e.target.value || 0),
                  })
                }
                style={{ ...input, flex: 1 }}
              />
              <button style={btnBlue} onClick={onClearGlobalRemise}>Retirer</button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button style={btnBlue} onClick={onClose}>Terminer</button>
        </div>
      </div>
    </>,
    document.body
  );
}


// HT brut d'une ligne "standard"
const rowHT = (l) =>
  Math.max(0, Number(l?.quantite || 0)) * Math.max(0, Number(l?.prixUnitaire || 0));

// remise HT pour une ligne selon un objet remise {rowIndex, type: 'percent'|'amount', value}
const remiseLigneHT = (lIndex, lignes, remises) => {
  const base = lignes[lIndex];
  if (!base || base.type === "note" || base.type === "pagebreak") return 0;
  const baseHT = rowHT(base);
  const list = (remises || []).filter(r => Number(r.rowIndex) === lIndex);

  let total = 0;
  for (const r of list) {
    if (r.type === "percent") {
      total += baseHT * Math.max(0, Math.min(100, Number(r.value || 0))) / 100;
    } else {
      total += Math.max(0, Number(r.value || 0)); // sera borné juste après
    }
  }
  return Math.min(total, baseHT);
};

// calcule tous les totaux (HT, TVA, TTC) en tenant compte des remises ligne + globale
function computeTotals(lignes, remisesLignes, globalRemise) {
  // 1) Sous-total HT brut (lignes standard uniquement)
  const stdIndexes = lignes
    .map((l, i) => (l && l.type !== "note" && l.type !== "pagebreak" ? i : -1))
    .filter(i => i >= 0);

  const htBrut = stdIndexes.reduce((s, i) => s + rowHT(lignes[i]), 0);

  // 2) Remises LIGNE HT (par ligne)
  const remisesLignesParLigne = stdIndexes.map(i => remiseLigneHT(i, lignes, remisesLignes));
  const htApresRemisesLignesParLigne = stdIndexes.map((i, k) => {
    const base = rowHT(lignes[i]);
    return Math.max(0, base - remisesLignesParLigne[k]);
  });
  const htApresRemisesLignes = htApresRemisesLignesParLigne.reduce((a, b) => a + b, 0);

  // 3) Remise GLOBALE en HT (dynamiquement, et jamais plus que le restant)
  let remiseGlobaleHT = 0;
  const mode = globalRemise?.mode || "percent";
  const gVal = Number(globalRemise?.value || 0);

  if (globalRemise?.enabled && gVal > 0 && htApresRemisesLignes > 0) {
    if (mode === "percent") {
      remiseGlobaleHT = (htApresRemisesLignes * Math.max(0, Math.min(100, gVal))) / 100;
    } else if (mode === "ht") {
      remiseGlobaleHT = Math.min(gVal, htApresRemisesLignes);
    } else if (mode === "ttc") {
      // Convertit TTC -> HT avec un taux effectif moyen de TVA (pondéré par HT net des lignes)
      const tvaEff =
        stdIndexes.reduce((s, i, k) => s + (htApresRemisesLignesParLigne[k] * Number(lignes[i].tva || 0)), 0) /
        Math.max(1, htApresRemisesLignes); // % moyen
      const factor = 1 + Math.max(0, tvaEff) / 100;
      const htFromTtc = gVal / factor;
      remiseGlobaleHT = Math.min(htFromTtc, htApresRemisesLignes);
    }
  }

  // 4) Répartition de la remise globale par ligne (pour calculer une TVA exacte)
  //    - percent : même ratio pour chaque ligne
  //    - montant : prorata du HT après remises-ligne
  let htFinalParLigne = [...htApresRemisesLignesParLigne];
  if (remiseGlobaleHT > 0) {
    if (mode === "percent") {
      const ratio = Math.max(0, Math.min(100, gVal)) / 100;
      htFinalParLigne = htFinalParLigne.map(v => Math.max(0, v * (1 - ratio)));
    } else {
      const base = Math.max(1e-9, htApresRemisesLignes);
      htFinalParLigne = htFinalParLigne.map(v => {
        const part = (v / base) * remiseGlobaleHT;
        return Math.max(0, v - part);
      });
    }
  }

  // 5) TVA totale (appliquée par ligne)
  const tvaTotale = stdIndexes.reduce((s, i, k) => {
    const taux = Math.max(0, Number(lignes[i].tva || 0)) / 100;
    return s + htFinalParLigne[k] * taux;
  }, 0);

  // 6) Résumé
  const totalRemisesLignesHT = remisesLignesParLigne.reduce((a, b) => a + b, 0);
  const htNet = htFinalParLigne.reduce((a, b) => a + b, 0);
  const ttc = htNet + tvaTotale;

  return {
    htBrut,
    totalRemisesLignesHT,
    remiseGlobaleHT,
    htNet,
    tvaTotale,
    ttc,
  };
}

/* ===================== Composant principal ===================== */
export default function TableauStyleVertical({
  lignes = [],
  handleChange,
  supprimerLigne,
  totalLigne, // optionnel
  maxPageHeight = 600,
  lectureSeule = false,
  onReorder,
  pageBreakGap = 16,
  societeId,
  projectName,
  isPdf = false,
  pdfBleedMm = 18,
  onLastPageHeight,
  tvaParDefaut = 20,
  showRemisesModal = false,
  onCloseRemises = () => { },
  globalRemise,
  onChangeGlobalRemise,
  onClearGlobalRemise,
  remisesLignes = [],
  onRemisesLignesChange,
  theme,
  numeroDocument = "",

}) {
  // Les couleurs viennent des CSS vars posées par le parent
  const vline = "var(--doc-vline)";

  // base TD (remplace l'ancien cellStyle pour les TD)
  const tdBase = {
    borderLeft: `0.5px solid ${vline}`,
    borderRight: `0.5px solid ${vline}`,
    padding: "4px 6px",
    verticalAlign: "middle",
    textAlign: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  // TH (entête) — fond + texte + bordures
  const headCell = { ...tdBase, background: "var(--doc-th-bg)", color: "var(--doc-th-text)", borderBottom: `1px solid ${vline}` };

  // variantes dérivées (remplacent les *CellStyle existants)
  const desigTdCell = { ...tdBase, textAlign: "left", whiteSpace: "normal", wordBreak: "break-word", overflow: "visible", position: "relative" };
  const quantiteTdCell = { ...tdBase, minWidth: "50px", width: "60px" };
  const puTdCell = { ...tdBase, minWidth: "60px", width: "70px" };
  const tvaTdCell = { ...tdBase, minWidth: "50px", width: "60px" };
  /** ---------- état & refs ---------- */
  const [pages, setPages] = useState([]);
  const [breakIndexForPage, setBreakIndexForPage] = useState([]);
  const [pageHeights, setPageHeights] = useState([]);
  const rowRefs = useRef([]);
  const [uiAddRemiseOpen, setUiAddRemiseOpen] = useState(false);
  const [tmpRem, setTmpRem] = useState({
    rowIndex: 0,
    type: "percent",
    value: "",
    label: "",
  });
  const [editingUniteIndex, setEditingUniteIndex] = useState(null);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingHtmlIndex, setEditingHtmlIndex] = useState(null);
  const [editedValues, setEditedValues] = useState({});
    // Numérotation propre : 1, 2, 3… uniquement pour les lignes standard
  const numeroParLigne = useMemo(() => {
    const map = {};
    let compteur = 1;
    lignes.forEach((l, i) => {
      if (!l || l.type === "note" || l.type === "pagebreak") return;
      map[i] = compteur++;
    });
    return map;
  }, [lignes]);

  useEffect(() => {
    setEditedValues((prev) => {
      const next = { ...prev };

      lignes.forEach((l, i) => {
        if (!next[i]) {
          next[i] = {
            quantite:
              l && l.quantite !== undefined && l.quantite !== null
                ? String(l.quantite)
                : "",
            prixUnitaire:
              l && l.prixUnitaire !== undefined && l.prixUnitaire !== null
                ? String(l.prixUnitaire)
                : "",
            tva:
              l && l.tva !== undefined && l.tva !== null
                ? String(l.tva)
                : "",
          };
        }
      });

      // Nettoyer les entrées qui ne correspondent plus à une ligne
      Object.keys(next).forEach((k) => {
        const idx = parseInt(k, 10);
        if (idx >= lignes.length) delete next[idx];
      });

      return next;
    });
  }, [lignes.length]);

  // ferme l’éditeur HTML au clic extérieur (comme pour Note)
  useEffect(() => {
    const close = (e) => {
      if (
        editingHtmlIndex !== null &&
        !e.target.closest?.('[data-html-editor="1"]')
      ) {
        setEditingHtmlIndex(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [editingHtmlIndex]);

  // --- suggestions inline (état) ---
  const [sug, setSug] = useState({ row: null, list: [], idx: -1 });
  const textareasRef = useRef([]);
  const [sugRect, setSugRect] = useState(null);
// Dès que les lignes OU la pagination changent,
// on redimensionne tous les textarea de désignation existants.
useLayoutEffect(() => {
  if (!Array.isArray(lignes) || !lignes.length) return;

  requestAnimationFrame(() => {
    // On ne traite que les index réellement présents dans les pages
    pages.forEach((pageRows) => {
      pageRows.forEach((rowIndex) => {
        const el = textareasRef.current[rowIndex];
        if (el) autoResizeTextarea(el);
      });
    });
  });
}, [lignes, pages]);


  const updateSugPosition = useCallback((rowIndex) => {
    const el = textareasRef.current[rowIndex];
    if (!el) return;
    const r = el.getBoundingClientRect();
    setSugRect({
      top: r.top,
      bottom: r.bottom,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, []);

  // Repositionne l’overlay au scroll / resize tant qu’un menu est ouvert
  useEffect(() => {
    if (sug.row == null) return;
    const onScrollOrResize = () => updateSugPosition(sug.row);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [sug.row, updateSugPosition]);

  const fetchSuggestionsRef = useRef(
    debounce(async (rowIndex, term) => {
      const raw = String(term || "").trim();
      if (raw.length < 1) {
        setSug({ row: null, list: [], idx: -1 });
        return;
      }

      try {
        const { data } = await axiosInstance.get("/designations", {
          params: {
            q: raw,
            societe_id: societeId ?? undefined,
            project: projectName || undefined,
          },
        });
        const list = (Array.isArray(data) ? data : []).map((r) => ({
          id: r.id,
          label: r.label ?? r.titre ?? "",
          prixUnitaire: Number(r.prixUnitaire ?? r.prix ?? 0),
          tva: r.tva !== undefined ? Number(r.tva) : undefined,
        }));

        // petit tri : d’abord les débuts de texte, puis les inclusions
        const nq = norm(raw);
        const starts = [];
        const contains = [];
        for (const d of list) {
          const nl = norm(d.label || "");
          if (nl.startsWith(nq)) starts.push(d);
          else if (nl.includes(nq)) contains.push(d);
        }
        const filtered = [...starts, ...contains].slice(0, 40);
        setSug({ row: rowIndex, list: filtered, idx: filtered.length ? 0 : -1 });
      } catch {
        setSug({ row: null, list: [], idx: -1 });
      }
    }, 150)
  );

  const fetchSuggestions = (rowIndex, term) =>
    fetchSuggestionsRef.current(rowIndex, term);

  const applySuggestion = (rowIndex, d) => {
    const prix = Number(d.prixUnitaire || 0);
    const tva =
      d.tva === 0 || Number.isFinite(d.tva)
        ? Number(d.tva)
        : Number(tvaParDefaut);

    handleChange(rowIndex, "designation", d.label); // peut être HTML
    handleChange(rowIndex, "prixUnitaire", prix);
    handleChange(rowIndex, "tva", tva);

    // met aussi à jour ce que voit l'utilisateur dans les champs
    setEditedValues((prev) => ({
      ...prev,
      [rowIndex]: {
        ...(prev[rowIndex] || {}),
        prixUnitaire: String(prix),
        tva: String(tva),
      },
    }));

    setSug({ row: null, list: [], idx: -1 });
  };

  // ajout rapide d’une ligne désignation
  const defaultLine = useMemo(
    () => ({
      type: "main_oeuvre",
      designation: "",
      quantite: 1,
      unite: "u",
      prixUnitaire: 0,
      tva: Number.isFinite(Number(tvaParDefaut)) ? Number(tvaParDefaut) : 20,
    }),
    [tvaParDefaut]
  );

  const addDesignationRow = () => {
    if (lectureSeule) return;
    const newIndex = lignes.length;
    applyRows([...lignes, { ...defaultLine }]);
    setTimeout(() => {
      const el = rowRefs.current[newIndex]?.querySelector("textarea");
      el && el.focus();
    }, 0);
  };

  // Renvoie le montant HT d'une remise appliquée à une ligne (toujours positif ici)
  function computeRemiseLigneHT(remise, lignes) {
    const base = lignes?.[remise.rowIndex];
    if (!base || base.type === "note" || base.type === "pagebreak") return 0;

    const baseHT = rowHT(base);
    if (remise.type === "percent") {
      const ratio = Math.max(0, Math.min(100, Number(remise.value || 0))) / 100;
      return Number((baseHT * ratio).toFixed(2));
    }
    // "amount" en €
    const val = Math.max(0, Number(remise.value || 0));
    return Number(Math.min(val, baseHT).toFixed(2));
  }

  /** ---------- helpers ---------- */
  const applyRows = (next) =>
    onReorder
      ? onReorder(next)
      : console.warn("onReorder manquant (passe setLignes au parent)");

  const formatPrix = (val) =>
    new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(val) ? val : 0);
  const normalizeNumber = (raw) => {
    const s = String(raw ?? "")
      .trim()
      .replace(",", ".")
      .replace(/\s/g, "");
    if (!s) return "";
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : "";
  };

  const safeValue = (val) =>
    typeof val === "number" && !isNaN(val) ? val : val === 0 ? 0 : "";

  // Accepte : 1, 1.5, 1,5, 0,25, .5, etc. (virgule OU point)
  const isNumeric = (val) => {
    const s = String(val ?? "").trim();
    if (s === "") return true;
    return /^-?\d*(?:[.,]\d*)?$/.test(s);
  };

  // Convertit "1,89" → 1.89 (Number) pour les calculs
  const parseNumeric = (val) => {
    const s = String(val ?? "").trim().replace(",", ".");
    if (s === "") return "";
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : "";
  };



  /** ---------- Drag & Drop ---------- */
  const setDragPayload = (e, payload) => {
    try {
      e.dataTransfer.setData("application/json", JSON.stringify(payload));
    } catch {
      e.dataTransfer.setData("text/plain", String(payload.index ?? ""));
    }
    e.dataTransfer.effectAllowed = "move";
  };
  const getDragPayload = (e) => {
    const j = e.dataTransfer.getData("application/json");
    if (j) {
      try {
        return JSON.parse(j);
      } catch { }
    }
    const p = e.dataTransfer.getData("text/plain");
    if (p && /^\d+$/.test(p)) return { type: "row", index: parseInt(p, 10) };
    return null;
  };
  const moveItem = (arr, from, to) => {
    if (from === to) return arr;
    const copy = [...arr];
    const [it] = copy.splice(from, 1);
    let target = to;
    if (from < to) target -= 1;
    copy.splice(target, 0, it);
    return copy;
  };

  const onRowDragStart = (rowIndex) => (e) =>
    setDragPayload(e, { type: "row", index: rowIndex });
  const onBreakDragStart = (breakIdx) => (e) =>
    setDragPayload(e, { type: "break", index: breakIdx });
  const onDragOver = () => (e) => {
    if (!lectureSeule) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    }
  };
  const onDropOnRow = (targetRowIndex) => (e) => {
    if (lectureSeule) return;
    e.preventDefault();
    const p = getDragPayload(e);
    if (!p) return;
    if (p.type === "row" || p.type === "break") {
      applyRows(moveItem(lignes, p.index, targetRowIndex));
    }
  };
  const onDropOnBreakRow = (breakIdx) => (e) => {
    if (lectureSeule) return;
    e.preventDefault();
    const p = getDragPayload(e);
    if (!p) return;
    if (p.type === "row") applyRows(moveItem(lignes, p.index, breakIdx));
  };

  /** ---------- Fermeture WYSIWYG note au clic extérieur ---------- */
  useEffect(() => {
    const close = (e) => {
      if (editingNoteIndex !== null && !e.target.closest?.('[data-note-editor="1"]')) {
        setEditingNoteIndex(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [editingNoteIndex]);

  // Normalisation TVA au premier rendu
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    if (!Array.isArray(lignes) || !lignes.length) return;
    let changed = false;
    const next = lignes.map((l) => {
      if (!l || l.type === "note" || l.type === "pagebreak") return l;
      const cur = Number(l.tva);
      if (!Number.isFinite(cur)) {
        changed = true;
        return { ...l, tva: Number(tvaParDefaut) };
      }
      return l;
    });
    if (changed) applyRows(next);
  }, [lignes, tvaParDefaut]);

  /** ---------- Sync si tvaParDefaut change ---------- */
  const prevTvaDefRef = useRef(tvaParDefaut);
  useEffect(() => {
    const prev = Number(prevTvaDefRef.current);
    const now = Number(tvaParDefaut);
    if (prev === now) return;
    if (!Array.isArray(lignes) || !lignes.length) {
      prevTvaDefRef.current = now;
      return;
    }
    let changed = false;
    const next = lignes.map((l) => {
      if (!l || l.type === "note" || l.type === "pagebreak") return l;
      const cur = Number(l.tva);
      if (!Number.isFinite(cur) || cur === prev) {
        changed = true;
        return { ...l, tva: now };
      }
      return l;
    });
    if (changed) applyRows(next);
    prevTvaDefRef.current = now;
  }, [tvaParDefaut, lignes]);

  useEffect(() => {
    if (!Array.isArray(lignes)) {
      setPages([[]]);
      setPageHeights([]);
      setBreakIndexForPage([]);
      return;
    }

    // Pagination unique pour édition + lectureSeule
    // - respecte les lignes { type: "pagebreak" } comme coupures manuelles
    // - coupe automatiquement quand la hauteur max est dépassée
    let currentHeight = 0;
    let currentPage = [];
    const newPages = [];
    const heights = [];
    const breakIdxPerPage = [];
    let endsWithBreak = false;

    for (let i = 0; i < lignes.length; i++) {
      const l = lignes[i];

      // LIGNE "pagebreak" → on termine la page en cours, on stocke l'index
      if (l?.type === "pagebreak") {
        endsWithBreak = i === lignes.length - 1;

        if (currentPage.length) {
          newPages.push(currentPage);
          heights.push(currentHeight);
          breakIdxPerPage.push(i); // coupure manuelle
          currentPage = [];
          currentHeight = 0;
        }
        continue;
      }

      endsWithBreak = false;

      // Hauteur de la ligne (ou fallback)
      const fallback = 30;
      const rowHeight =
        rowRefs.current[i]?.getBoundingClientRect()?.height || fallback;

      // Coupure auto si on dépasse la hauteur max
      if (currentHeight + rowHeight > maxPageHeight && currentPage.length) {
        newPages.push(currentPage);
        heights.push(currentHeight);
        breakIdxPerPage.push(null); // coupure auto
        currentPage = [];
        currentHeight = 0;
      }

      currentPage.push(i);
      currentHeight += rowHeight;
    }

    // Dernière page
    if (currentPage.length) {
      newPages.push(currentPage);
      heights.push(currentHeight);
      breakIdxPerPage.push(null);
    } else if (endsWithBreak) {
      // Cas rare : on termine par un pagebreak
      newPages.push([]);
      heights.push(0);
      breakIdxPerPage.push(null);
    }

    setPages(newPages);
    setPageHeights(heights);
    setBreakIndexForPage(breakIdxPerPage);
  }, [lignes, maxPageHeight]);

  // remonte la hauteur de la dernière page
  useEffect(() => {
    if (typeof onLastPageHeight === "function" && pageHeights.length) {
      onLastPageHeight(pageHeights[pageHeights.length - 1] || 0);
    }
  }, [pageHeights, onLastPageHeight]);

  /** ---------- Petits boutons ---------- */
  const btnSm = {
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 6,
    background: "#e5e7eb",
    border: "1px solid #d1d5db",
    color: "#111827",
    cursor: "pointer",
  };
  const addNoteRow = () => {
    if (lectureSeule) return;
    const idx = lignes.length;
    applyRows([...lignes, { type: "note", designation: "" }]);
    setTimeout(() => setEditingNoteIndex(idx), 0);
  };

  // Empêche deux sauts collés
  const addPageBreak = () => {
    if (lectureSeule) return;
    const last = lignes[lignes.length - 1];
    if (last?.type === "pagebreak") return;
    applyRows([...lignes, { type: "pagebreak" }]);
  };
  const totalPages = pages && pages.length ? pages.length : 1;
  /** ---------- Élargissement PDF ---------- */
  const bleedHalf = isPdf ? `${pdfBleedMm / 2}mm` : 0;


  /** ------- Rendu ------- */
  return (
    <div
      style={{
        overflowX: "hidden",
        position: "relative",
        zIndex: 0,
        paddingLeft: 0,
        paddingRight: 0,
        marginLeft: isPdf ? `-${bleedHalf}` : 0,
        marginRight: isPdf ? `-${bleedHalf}` : 0,
      }}
    >
      {pages.map((pageRows, pageIndex) => (
        <div
          key={pageIndex}
          // en PDF, chaque "page" de tableau à partir de la 2e
          // commence sur une NOUVELLE page du PDF
          className={isPdf && pageIndex > 0 ? "pdf-page-break" : ""}
          style={{ marginBottom: "12px" }}
        >
          {/* TABLEAU (une table par page) */}
          <table
            className="table-pdf"
            style={{
              ...tableStyle,
              width: isPdf ? `calc(100% + ${pdfBleedMm}mm)` : "100%",
            }}
          >
            <colgroup>
              <col className="col--num" style={{ width: "5%" }} />
              <col className="col--libelle" style={{ width: "60%" }} />
              <col className="col--qte" style={{ width: "7%" }} />
              <col className="col--pu" style={{ width: "8%" }} />
              <col className="col--tva" style={{ width: "7%" }} />
              <col className="col--total" style={{ width: "10%" }} />
              {!lectureSeule && (
                <col className="col--actions" style={{ width: "3%" }} />
              )}
            </colgroup>

            {/* En-tête :
              - toujours affiché dans l’app (isPdf === false)
              - en PDF : seulement sur la 1ʳᵉ page (pageIndex === 0)
          */}
            {(!isPdf || pageIndex === 0) && (
              <thead>
                <tr>
                  <th className="col--num" style={headCell}>
                    N°
                  </th>
                  <th
                    className="col--libelle"
                    style={{ ...headCell, textAlign: "left" }}
                  >
                    Désignation
                  </th>
                  <th className="col--qte" style={headCell}>
                    Qté
                  </th>
                  <th className="col--pu" style={headCell}>
                    PU HT
                  </th>
                  <th className="col--tva" style={headCell}>
                    TVA
                  </th>
                  <th className="col--total" style={headCell}>
                    Total HT
                  </th>
                  {!lectureSeule && (
                    <th className="col--actions" style={headCell}></th>
                  )}
                </tr>
              </thead>
            )}

            <tbody>
              {pageRows.map((rowIndex) => {
                const ligne = lignes[rowIndex] || {};

                // ===== NOTE
                if (ligne.type === "note") {
                  return (
                  <NoteRow
  key={`note-${rowIndex}`}
  rowIndex={rowIndex}
  ligne={ligne}
  lectureSeule={lectureSeule}
  onEditOpen={setEditingNoteIndex}
  isEditing={!lectureSeule && editingNoteIndex === rowIndex}
  onDone={() => setEditingNoteIndex(null)}
  onDelete={() => supprimerLigne(rowIndex)}
  handleChange={handleChange}
  rowRef={(el) => (rowRefs.current[rowIndex] = el)}
  isPdf={isPdf}
  tdBase={tdBase} 
/>

                  );
                }

                // ===== LIGNE STANDARD
                return (
               <tr
  key={rowIndex}
  ref={(el) => (rowRefs.current[rowIndex] = el)}
  draggable={!lectureSeule}
  onDragStart={onRowDragStart(rowIndex)}
  onDragOver={onDragOver()}
  onDrop={onDropOnRow(rowIndex)}
>


                <td className="col--num" style={tdBase}>
  {numeroParLigne[rowIndex] ?? ""}
</td>




                    {/* Désignation */}
                    <td className="col--libelle" style={desigTdCell}>
                      {lectureSeule ? (
                        isProbablyHtml(ligne.designation) ? (
                          <div
                            className="ql-editor !p-0"
                            style={{
                              lineHeight: 1.35,
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                ensureHtml(ligne.designation)
                              ),
                            }}
                          />
                        ) : (
                          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                            {ligne.designation}
                          </div>
                        )
                      ) : isProbablyHtml(ligne.designation) ? (
                        editingHtmlIndex === rowIndex ? (
                          <div data-html-editor="1">
                            <div
                              id={`toolbar-des-${rowIndex}`}
                              className="ql-toolbar ql-snow rounded-t-md mb-1"
                            >
                              <select className="ql-size" defaultValue="">
                                <option value="">Défaut</option>
                                {SIZE_OPTIONS.map((v) => (
                                  <option key={v} value={v}>
                                    {parseInt(v, 10)}
                                  </option>
                                ))}
                              </select>
                              <button className="ql-bold" />
                              <button className="ql-italic" />
                              <button className="ql-underline" />
                              <select className="ql-align" />
                              <button className="ql-list" value="ordered" />
                              <button className="ql-list" value="bullet" />
                              <button className="ql-link" />
                              <button className="ql-clean" />
                              <button
                                type="button"
                                className="ml-2 text-xs px-2 py-1 rounded border"
                                onClick={() => setEditingHtmlIndex(null)}
                              >
                                Terminer
                              </button>
                            </div>
                            <ReactQuill
                              theme="snow"
                              value={ensureHtml(ligne.designation)}
                              onChange={(html) =>
                                handleChange(rowIndex, "designation", html)
                              }
                              modules={{
                                toolbar: { container: `#toolbar-des-${rowIndex}` },
                              }}
                              formats={quillFormats}
                              className="rounded-b-md bg-white"
                            />
                          </div>
                        ) : (
                          <div
                            className="ql-editor !p-0 cursor-text"
                            style={{ lineHeight: 1.4 }}
                            onClick={() => setEditingHtmlIndex(rowIndex)}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                ensureHtml(ligne.designation)
                              ),
                            }}
                          />
                        )
             ) : (
  <div style={{ position: "relative", zIndex: 2 }}>
          <textarea
        ref={(el) => {
          textareasRef.current[rowIndex] = el;
          autoResizeTextarea(el); // ajuster dès le montage
        }}
        value={ligne.designation || ""}
        onFocus={() => updateSugPosition(rowIndex)}
        onChange={(e) => {
          const v = e.target.value;
          handleChange(rowIndex, "designation", v);

          // auto-resize propre à chaque frappe
          autoResizeTextarea(e.target);

          const firstLine = (v || "").split("\n")[0].trim();
          updateSugPosition(rowIndex);
          fetchSuggestions(rowIndex, firstLine);
        }}
        onKeyDown={(e) => {
          if (sug.row === rowIndex && sug.list.length) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setSug((s) => ({
                ...s,
                idx: Math.min(
                  s.list.length - 1,
                  Math.max(0, s.idx + 1)
                ),
              }));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setSug((s) => ({
                ...s,
                idx: Math.max(0, s.idx - 1),
              }));
            } else if (e.key === "Enter" && sug.idx >= 0) {
              e.preventDefault();
              applySuggestion(rowIndex, sug.list[sug.idx]);
            } else if (e.key === "Escape") {
              setSug({ row: null, list: [], idx: -1 });
            }
          }
        }}
        placeholder="Écrire une désignation…"
        style={designationStyle}
        rows={1}
        onBlur={() =>
          setTimeout(
            () => setSug({ row: null, list: [], idx: -1 }),
            200
          )
        }
      />
  </div>
)
}

                    </td>

                    {/* Qté + unité */}
                    <td className="col--qte" style={quantiteTdCell}>
                      {lectureSeule ? (
                        <span>
                          {ligne.quantite} {ligne.unite || "u"}
                        </span>
                      ) : (
                        <div style={numericWrapper}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editedValues[rowIndex]?.quantite ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;

                              // on autorise uniquement chiffres, . et ,
                              if (!/^[0-9.,]*$/.test(raw)) return;

                              // Met à jour ce que voit l'utilisateur
                              setEditedValues((prev) => ({
                                ...prev,
                                [rowIndex]: {
                                  ...(prev[rowIndex] || {}),
                                  quantite: raw,
                                },
                              }));

                              // Valeur numérique pour les calculs (0 si vide ou invalide)
                              const num = normalizeNumber(raw);
                              handleChange(
                                rowIndex,
                                "quantite",
                                num === "" ? 0 : num
                              );
                            }}
                            style={{
                              ...numericInputStyle,
                              width: `${(editedValues[rowIndex]?.quantite || "0").length + 1}ch`,
                            }}
                          />


                          {editingUniteIndex === rowIndex ? (
                            <input
                              type="text"
                              list="tableau-unites"
                              autoFocus
                              value={ligne.unite || ""}
                              onBlur={() => setEditingUniteIndex(null)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && e.currentTarget.blur()
                              }
                              onChange={(e) =>
                                handleChange(
                                  rowIndex,
                                  "unite",
                                  e.target.value
                                )
                              }
                              style={unitInputListStyle}
                              placeholder="u"
                            />
                          ) : (
                            <span
                              style={{
                                ...unitSuffixStyle,
                                cursor: lectureSeule ? "default" : "pointer",
                              }}
                              onClick={() =>
                                !lectureSeule &&
                                setEditingUniteIndex(rowIndex)
                              }
                              title="Choisir une unité"
                            >
                              {ligne.unite || "u"}
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* PU */}
                    <td className="col--pu" style={puTdCell}>
                      {lectureSeule ? (
                        <span>{formatPrix(ligne.prixUnitaire)} €</span>
                      ) : (
                        <div style={numericWrapper}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editedValues[rowIndex]?.prixUnitaire ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (!/^[0-9.,]*$/.test(raw)) return;

                              setEditedValues((prev) => ({
                                ...prev,
                                [rowIndex]: {
                                  ...(prev[rowIndex] || {}),
                                  prixUnitaire: raw,
                                },
                              }));

                              const num = normalizeNumber(raw);
                              handleChange(
                                rowIndex,
                                "prixUnitaire",
                                num === "" ? 0 : num
                              );
                            }}
                            style={{
                              ...numericInputStyle,
                              width: `${(editedValues[rowIndex]?.prixUnitaire || "0").length + 1}ch`,
                            }}
                          />


                          <span style={unitSuffixStyle}>€</span>
                        </div>
                      )}
                    </td>

                    {/* TVA */}
                    <td className="col--tva" style={tvaTdCell}>
                      {lectureSeule ? (
                        <span>{ligne.tva} %</span>
                      ) : (
                        <div style={numericWrapper}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editedValues[rowIndex]?.tva ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (!/^[0-9.,]*$/.test(raw)) return;

                              setEditedValues((prev) => ({
                                ...prev,
                                [rowIndex]: {
                                  ...(prev[rowIndex] || {}),
                                  tva: raw,
                                },
                              }));

                              const num = normalizeNumber(raw);
                              handleChange(rowIndex, "tva", num === "" ? 0 : num);
                            }}
                            style={{
                              ...numericInputStyle,
                              width: `${(editedValues[rowIndex]?.tva || "0").length + 1}ch`,
                            }}
                          />


                          <span style={unitSuffixStyle}>%</span>
                        </div>
                      )}
                    </td>

                    {/* Total HT */}
                    <td
                      className="col--total"
                      style={{
                        ...tdBase,
                        textAlign: "right",
                        overflow: "visible",
                        textOverflow: "clip",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>
                        {formatPrix(
                          Number(
                            (ligne.quantite || 0) * (ligne.prixUnitaire || 0)
                          )
                        )}{" "}
                        €
                      </span>
                    </td>

                    {/* Actions (X) */}
                    {!lectureSeule && (
                      <td className="col--actions" style={tdBase}>
                        <button
                          onClick={() => supprimerLigne(rowIndex)}
                          style={deleteBtnStyle}
                          title="Supprimer"
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <datalist id="tableau-unites">
            {UNIT_OPTIONS.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>

          {/* Barre “Saut de page” entre tableaux (édition uniquement) */}
          {pageIndex < pages.length - 1 &&
            (!lectureSeule ? (
              <div
                className="no-print"
                draggable={!lectureSeule}
                onDragStart={onBreakDragStart(breakIndexForPage[pageIndex])}
                onDragOver={onDragOver()}
                onDrop={onDropOnBreakRow(breakIndexForPage[pageIndex])}
                style={pageBreakBarStyle}
                title="Glissez des lignes ici pour déplacer le saut"
              >
                <span style={{ pointerEvents: "none" }}>— Saut de page —</span>
                <button
                  type="button"
                  onClick={() => {
                    const idx = breakIndexForPage[pageIndex];
                    if (typeof idx === "number") supprimerLigne(idx);
                  }}
                  style={chipBtn}
                >
                  Supprimer
                </button>
              </div>
            ) : (
              <div style={{ height: pageBreakGap }} />
            ))}

          {/* Barre d’ajout sous le DERNIER tableau */}
          {pageIndex === pages.length - 1 && !lectureSeule && (
            <div style={{ marginTop: 8 }}>
              {/* Ligne de boutons + formulaire remise */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button onClick={addDesignationRow} style={btnSm}>
                  Désignation
                </button>
                <button onClick={addNoteRow} style={btnSm}>
                  Note
                </button>
                <button onClick={addPageBreak} style={btnSm}>
                  Ajouter un tableau
                </button>

                <SuggestionsOverlay
                  rect={sug.row != null ? sugRect : null}
                  list={sug.list}
                  activeIdx={sug.idx}
                  onHover={(i) => setSug((s) => ({ ...s, idx: i }))}
                  onPick={(d) => applySuggestion(sug.row, d)}
                  onClose={() =>
                    setSug({ row: null, list: [], idx: -1 })
                  }
                  tvaParDefaut={tvaParDefaut}
                  isPdf={isPdf}
                />

                {/* Remise (ligne) */}
                {uiAddRemiseOpen && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: "6px 8px",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontSize: 12 }}>Désig. N°</span>
                      <input
                        type="number"
                        min={1}
                        max={lignes.length}
                        value={Number(tmpRem.rowIndex ?? 0) + 1}
                        onChange={(e) => {
                          const n = Math.max(
                            1,
                            Math.min(
                              lignes.length || 1,
                              parseInt(e.target.value || "1", 10)
                            )
                          );
                          setTmpRem((s) => ({ ...s, rowIndex: n - 1 }));
                        }}
                        style={{
                          width: 64,
                          height: 28,
                          padding: "0 6px",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                        }}
                      />
                    </div>

                    <select
                      value={tmpRem.type}
                      onChange={(e) =>
                        setTmpRem((s) => ({ ...s, type: e.target.value }))
                      }
                      style={{
                        height: 28,
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        padding: "0 6px",
                      }}
                    >
                      <option value="percent">%</option>
                      <option value="amount">€</option>
                    </select>

                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={
                        tmpRem.type === "percent" ? "0-100" : "montant"
                      }
                      value={tmpRem.value}
                      onChange={(e) =>
                        setTmpRem((s) => ({ ...s, value: e.target.value }))
                      }
                      style={{
                        width: 100,
                        height: 28,
                        padding: "0 6px",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                      }}
                    />

                    <input
                      type="text"
                      placeholder="Libellé (optionnel)"
                      value={tmpRem.label || ""}
                      onChange={(e) =>
                        setTmpRem((s) => ({ ...s, label: e.target.value }))
                      }
                      style={{
                        width: 180,
                        height: 28,
                        padding: "0 6px",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                      }}
                    />

                    <button
                      type="button"
                      style={{ ...btnSm, background: "#111827", color: "#fff" }}
                      onClick={() => {
                        const idx = Number(tmpRem.rowIndex || 0);
                        if (
                          !Number.isFinite(idx) ||
                          idx < 0 ||
                          idx >= lignes.length
                        )
                          return;
                        const v = Number(tmpRem.value);
                        if (!Number.isFinite(v)) return;

                        let payload = {
                          rowIndex: idx,
                          type: tmpRem.type,
                          value: v,
                          label: (tmpRem.label || "").trim(),
                        };
                        if (payload.type === "percent") {
                          payload.value = Math.max(
                            0,
                            Math.min(100, payload.value)
                          );
                        } else {
                          payload.value = Math.max(0, payload.value);
                        }
                        const next = [...(remisesLignes || []), payload];
                        onRemisesLignesChange &&
                          onRemisesLignesChange(next);
                        setTmpRem((s) => ({ ...s, value: "" }));
                      }}
                    >
                      Ajouter
                    </button>
                  </div>
                )}
              </div>

              {/* Liste des remises ligne */}
              {Array.isArray(remisesLignes) && remisesLignes.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                  }}
                >
                  {remisesLignes.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 8px",
                        borderTop: i ? "1px solid #f1f5f9" : "none",
                      }}
                    >
                      <div style={{ fontSize: 12 }}>
                        Remise désignation{" "}
                        <strong>N°{numeroParLigne[r.rowIndex] ?? (r.rowIndex + 1)}</strong>
                        {r.label ? <> — {r.label}</> : null}
                        {" · "}
                        {r.type === "percent"
                          ? `${r.value}%`
                          : `${Number(r.value).toFixed(2)} €`}
                      </div>

                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          type="button"
                          style={btnSm}
                          onClick={() => {
                            const next = remisesLignes.filter(
                              (_, k) => k !== i
                            );
                            onRemisesLignesChange &&
                              onRemisesLignesChange(next);
                          }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* === Bloc totaux unique (après les tableaux) === */}
                  {(() => {
                    const {
                      htBrut,
                      totalRemisesLignesHT,
                      remiseGlobaleHT,
                      htNet,
                      tvaTotale,
                      ttc,
                    } = computeTotals(
                      lignes,
                      remisesLignes,
                      globalRemise
                    );

                    const fmt = (n) =>
                      new Intl.NumberFormat("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(n);

                    return (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 12,
                        }}
                      >
                        <div style={{ width: 360, fontSize: 13 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              borderTop: "1px solid #e5e7eb",
                              padding: "6px 0",
                            }}
                          >
                            <span>Sous-total HT</span>
                            <span>{fmt(htBrut)} €</span>
                          </div>

                          {totalRemisesLignesHT > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "#b91c1c",
                                padding: "2px 0",
                              }}
                            >
                              <span>Remises désignation</span>
                              <span>- {fmt(totalRemisesLignesHT)} €</span>
                            </div>
                          )}

                          {remiseGlobaleHT > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "#b91c1c",
                                padding: "2px 0",
                              }}
                            >
                              <span>Remise globale</span>
                              <span>- {fmt(remiseGlobaleHT)} €</span>
                            </div>
                          )}

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontWeight: 600,
                              padding: "6px 0",
                            }}
                          >
                            <span>Sous-total après remise (HT)</span>
                            <span>{fmt(htNet)} €</span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "2px 0",
                            }}
                          >
                            <span>TVA</span>
                            <span>{fmt(tvaTotale)} €</span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "6px 0",
                              fontWeight: 700,
                            }}
                          >
                            <span>Total TTC</span>
                            <span>{fmt(ttc)} €</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
